if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.imageObserver = null;
        this.prefetchTimer = null;
        this.imageRequestId = 0;
        this.modalSessionId = 0;
      }

      hide() {
        this.modalSessionId += 1;
        this.disconnectImageObserver();
        window.clearTimeout(this.prefetchTimer);
        this.prefetchTimer = null;
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.modalSessionId += 1;
        this.showActiveMedia(this.modalSessionId);
      }

      showActiveMedia(modalSessionId = this.modalSessionId) {
        const mediaId = this.openedBy && this.openedBy.getAttribute('data-media-id');
        if (!mediaId) return;

        const activeMedia = this.findModalMedia(mediaId);
        if (!activeMedia) return;

        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
        });

        const activeImageReady = this.hydrateImage(activeMedia, {
          priority: 'high',
          useGalleryPreview: true,
        });

        this.loadDeferredMedia(activeMedia);
        activeMedia.scrollIntoView({ block: 'nearest', inline: 'center' });

        const container = this.querySelector('[role="document"]');
        if (container) {
          const mediaWidth = activeMedia.getBoundingClientRect().width;
          container.scrollLeft = Math.max(0, (mediaWidth - container.clientWidth) / 2);
        }

        activeImageReady.finally(() => {
          if (!this.hasAttribute('open')) return;
          if (this.modalSessionId !== modalSessionId) return;
          if (!activeMedia.classList.contains('active')) return;

          this.connectImageObserver(modalSessionId);
          this.scheduleAdjacentPrefetch(activeMedia, modalSessionId);
        });
      }

      findModalMedia(mediaId) {
        return Array.from(this.querySelectorAll('[data-media-id]')).find(
          (element) => element.getAttribute('data-media-id') === mediaId
        );
      }

      connectImageObserver(modalSessionId = this.modalSessionId) {
        this.disconnectImageObserver();

        if (!this.hasAttribute('open')) return;
        if (this.modalSessionId !== modalSessionId) return;
        if (!('IntersectionObserver' in window)) return;

        const container = this.querySelector('[role="document"]');
        if (!container) return;

        this.imageObserver = new IntersectionObserver(
          (entries) => {
            if (!this.hasAttribute('open')) return;
            if (this.modalSessionId !== modalSessionId) return;

            entries.forEach((entry) => {
              if (!entry.isIntersecting || entry.target.classList.contains('active')) return;
              this.hydrateImage(entry.target, {
                priority: 'low',
                useGalleryPreview: true,
              });
              this.imageObserver && this.imageObserver.unobserve(entry.target);
            });
          },
          {
            root: container,
            rootMargin: '800px 0px',
            threshold: 0.01,
          }
        );

        this.querySelectorAll('[data-modal-media-image]:not(.active)').forEach((element) => {
          this.imageObserver.observe(element);
        });
      }

      disconnectImageObserver() {
        if (this.imageObserver) this.imageObserver.disconnect();
        this.imageObserver = null;
      }

      hydrateImage(mediaElement, options = {}) {
        if (!mediaElement || !mediaElement.matches('[data-modal-media-image]')) return Promise.resolve();

        const priority = options.priority || 'low';
        const image = this.ensureImage(mediaElement, options);

        if (mediaElement.dataset.modalMediaLoaded === 'true') {
          return Promise.resolve();
        }

        if (mediaElement.dataset.modalMediaLoading === 'true') {
          if (priority === 'high' && mediaElement.dataset.modalMediaLoadingPriority !== 'high') {
            return this.loadZoomImage(mediaElement, image, priority);
          }

          return Promise.resolve();
        }

        return this.loadZoomImage(mediaElement, image, priority);
      }

      ensureImage(mediaElement, options = {}) {
        const existingImage = mediaElement.querySelector('[data-modal-media-image-element]');
        if (existingImage) {
          this.applyImagePriority(existingImage, options.priority || 'low');
          this.applyGalleryPreview(existingImage, mediaElement, options);
          return existingImage;
        }

        const image = document.createElement('img');
        image.className = 'global-media-settings global-media-settings--no-shadow';
        image.dataset.modalMediaImageElement = 'true';
        image.alt = mediaElement.dataset.alt || '';
        image.width = Number(mediaElement.dataset.zoomWidth || 1100);
        image.height = Number(mediaElement.dataset.zoomHeight || 1100);
        image.decoding = 'async';
        image.style.display = 'block';
        image.style.width = '100%';
        image.style.height = '100%';
        image.style.objectFit = 'contain';

        this.applyImagePriority(image, options.priority || 'low');
        this.applyGalleryPreview(image, mediaElement, options);

        mediaElement.appendChild(image);
        return image;
      }

      applyImagePriority(image, priority) {
        if (priority !== 'high' && image.getAttribute('fetchpriority') === 'high') return;

        image.loading = priority === 'high' ? 'eager' : 'lazy';
        image.setAttribute('fetchpriority', priority);
      }

      applyGalleryPreview(image, mediaElement, options) {
        if (!options.useGalleryPreview || image.getAttribute('src')) return;

        const previewSource = this.findGalleryPreviewSource(mediaElement.getAttribute('data-media-id'));
        if (previewSource) image.src = previewSource;
      }

      async loadZoomImage(mediaElement, image, priority) {
        const source = mediaElement.dataset.zoomSrc;
        if (!source) return;

        this.imageRequestId += 1;
        const requestId = String(this.imageRequestId);
        mediaElement.dataset.modalMediaLoading = 'true';
        mediaElement.dataset.modalMediaLoadingPriority = priority;
        mediaElement.dataset.modalMediaLoadId = requestId;

        try {
          const sourceSet = mediaElement.dataset.zoomSrcset || '';
          const sizes = mediaElement.dataset.zoomSizes || '100vw';
          const preloadImage = document.createElement('img');
          preloadImage.decoding = 'async';
          preloadImage.setAttribute('fetchpriority', priority);
          preloadImage.sizes = sizes;
          if (sourceSet) preloadImage.srcset = sourceSet;
          preloadImage.src = source;

          await this.waitForImage(preloadImage);

          if (mediaElement.dataset.modalMediaLoadId !== requestId) return;
          if (!preloadImage.naturalWidth) return;

          image.sizes = sizes;
          if (sourceSet) image.srcset = sourceSet;
          image.src = source;
          this.applyImagePriority(image, priority);

          mediaElement.dataset.modalMediaLoaded = 'true';
        } finally {
          if (mediaElement.dataset.modalMediaLoadId === requestId) {
            mediaElement.dataset.modalMediaLoading = 'false';
            delete mediaElement.dataset.modalMediaLoadingPriority;
            delete mediaElement.dataset.modalMediaLoadId;
          }
        }
      }

      async waitForImage(image) {
        if (typeof image.decode === 'function') {
          try {
            await image.decode();
          } catch (_error) {
            // Failed decodes fall back to the naturalWidth check in loadZoomImage().
          }
          return;
        }

        if (image.complete) return;

        await new Promise((resolve) => {
          image.onload = resolve;
          image.onerror = resolve;
        });
      }

      findGalleryPreviewSource(mediaId) {
        if (!mediaId) return '';

        const openedGalleryImage = this.openedBy?.closest('.product__media-item')?.querySelector('img');
        if (openedGalleryImage && this.openedBy?.getAttribute('data-media-id') === mediaId) {
          return openedGalleryImage.currentSrc || openedGalleryImage.src || '';
        }

        const galleryScope =
          this.openedBy?.closest('media-gallery') ||
          this.openedBy?.closest('.product__media-wrapper') ||
          this.openedBy?.closest('.product');
        const scopedOpener = this.findGalleryOpener(galleryScope, mediaId);
        const scopedGalleryImage = scopedOpener?.closest('.product__media-item')?.querySelector('img');
        if (scopedGalleryImage) return scopedGalleryImage.currentSrc || scopedGalleryImage.src || '';

        const opener = this.findGalleryOpener(document, mediaId);
        const galleryImage = opener?.closest('.product__media-item')?.querySelector('img');
        return galleryImage?.currentSrc || galleryImage?.src || '';
      }

      findGalleryOpener(root, mediaId) {
        if (!root) return null;

        return Array.from(root.querySelectorAll('.product__media-toggle[data-media-id]')).find(
          (element) => element.getAttribute('data-media-id') === mediaId
        );
      }

      scheduleAdjacentPrefetch(activeMedia, modalSessionId = this.modalSessionId) {
        window.clearTimeout(this.prefetchTimer);
        this.prefetchTimer = window.setTimeout(() => {
          this.prefetchTimer = null;

          if (!this.hasAttribute('open')) return;
          if (this.modalSessionId !== modalSessionId) return;
          if (!activeMedia.classList.contains('active')) return;

          this.prefetchAdjacent(activeMedia);
        }, 150);
      }

      prefetchAdjacent(activeMedia) {
        const items = Array.from(this.querySelectorAll('[data-media-id]'));
        const activeIndex = items.indexOf(activeMedia);
        if (activeIndex < 0) return;

        [items[activeIndex - 1], items[activeIndex + 1]].forEach((element) => {
          this.hydrateImage(element, {
            priority: 'low',
            useGalleryPreview: true,
          });
        });
      }

      loadDeferredMedia(activeMedia) {
        const template = activeMedia.querySelector('template');
        const content = template ? template.content : null;

        if (activeMedia.nodeName == 'DEFERRED-MEDIA' && content && content.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }
      }
    }
  );
}
